import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { constructUrl, getParams } from "@/app/_module/services/helper";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";
import { handleApiCall, normalizeApiError } from "@/app/_module/utils/apiResponseHandler";

import type { UpdateAppointment } from "../schema/appointment-schema";
import type { CreateAppointmentParams } from "../types/create-appointment";
import type { GetAppointmentByIdResponse, GetAppointmentsParams, GetAppointmentsResponse } from "../types/get-appointment-by-id";

// Enhanced error handling for appointments with normalized retry info
const handleAppointmentError = (error: unknown, operation: string, opts: { toast?: boolean } = {}) => {
  const normalized = normalizeApiError(error);
  const message = normalized.message || `Failed to ${operation}`;
  const retrySuffix = typeof normalized.retryAfterSeconds === 'number'
    ? ` (retry after ${normalized.retryAfterSeconds}s)`
    : '';

  // A 401 belongs to the axios interceptor (refresh or sign-out) — a toast on
  // top of that is noise. Silent MFA-window errors stay silent. And a failed
  // READ never toasts: the screen that asked renders its own error state with
  // a retry, so a phone on a flaky cell link doesn't get "Failed to fetch
  // appointments" popping over a list that is about to retry anyway.
  const is401 = normalized.code === 401 || String(normalized.code) === '401';
  if (opts.toast !== false && !normalized.silent && !is401) {
    showToast(`${message}${retrySuffix}`, "error");
  }
  const e = new Error(message) as Error & { code?: string | number; retryAfterSeconds?: number };
  e.code = normalized.code;
  e.retryAfterSeconds = normalized.retryAfterSeconds;
  throw e;
};

// Validate appointment data before API calls
const validateAppointmentData = (data: Partial<CreateAppointmentParams | UpdateAppointment>, operation: string) => {
  if (operation === 'create') {
    const createData = data as CreateAppointmentParams;
    if (!createData.title?.trim()) {
      throw new Error('Appointment title is required');
    }
    
    // Legacy fields are required from UI, new fields are auto-generated
    if (!createData.apptDate) {
      throw new Error('Appointment date is required');
    }
    if (!createData.time) {
      throw new Error('Appointment time is required');
    }
  }
  
  // Validate date format if provided
  {
    const maybe = data as Record<string, unknown>;
    const dateValue = typeof data.apptDate === 'string'
      ? data.apptDate
      : (typeof maybe.date === 'string' ? (maybe.date as string) : undefined);
    if (dateValue && isNaN(Date.parse(dateValue))) {
      throw new Error('Invalid appointment date format');
    }
  }
  
  // Validate time format if provided
  if (data.time) {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(data.time)) {
      throw new Error('Invalid time format. Use HH:MM format');
    }
  }
  
  // Validate new date/time fields
  {
    const maybe = data as Record<string, unknown>;
    const start = maybe.startTime;
    if (start !== undefined) {
      const startTime = new Date(typeof start === 'string' || start instanceof Date ? start : String(start));
      if (isNaN(startTime.getTime())) {
        throw new Error('Invalid start time format');
      }
    }
    const end = maybe.endTime;
    if (end !== undefined) {
      const endTime = new Date(typeof end === 'string' || end instanceof Date ? end : String(end));
      if (isNaN(endTime.getTime())) {
        throw new Error('Invalid end time format');
      }
    }
  }
  
  // Validate meeting link format if provided
  {
    const maybe = data as Record<string, unknown>;
    const link = maybe.meetingLink;
    if (typeof link === 'string' && link.trim()) {
      try {
        new URL(link);
      } catch {
        throw new Error('Invalid meeting link URL format');
      }
    }
  }
  
  // Validate UUID fields if provided
  {
    const maybe = data as Record<string, unknown>;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const leadId = maybe.leadId;
    if (typeof leadId === 'string' && leadId && !uuidRegex.test(leadId)) {
      throw new Error('Invalid lead ID format (must be UUID)');
    }
    const agencyId = maybe.agencyId;
    if (typeof agencyId === 'string' && agencyId && !uuidRegex.test(agencyId)) {
      throw new Error('Invalid agency ID format (must be UUID)');
    }
  }
};

export const runtime = "nodejs";

// Get all appointments with associations
export const getAppointments = async (param: GetAppointmentsParams): Promise<GetAppointmentsResponse> => {
  try {
    // Validate parameters
    if (param.page && param.page < 1) {
      throw new Error('Page number must be greater than 0');
    }
    if (param.limit && (param.limit < 1 || param.limit > 1000)) {
      throw new Error('Limit must be between 1 and 1000');
    }

    let params;
    if (param) {
      params = getParams<GetAppointmentsParams>(param);
    }

    const url = params
      ? constructUrl(getApiUrl(endpoint.appointments.getAppointments), params)
      : getApiUrl(endpoint.appointments.getAppointments);

    const result = await handleApiCall<GetAppointmentsResponse>(async () => {
      const response = await api.get<GetAppointmentsResponse>({
        url,
        auth: true, // Add authentication
      });
      return response;
    });

    // Handle different response structures after handleApiCall extraction
    let items: any[] = [];
    let meta: any = {};
    
    if ((result as any)?.items && Array.isArray((result as any).items)) {
      items = (result as any).items;
      meta = (result as any).data?.meta || {};
    } else if ((result as any)?.data?.items && Array.isArray((result as any).data.items)) {
      items = (result as any).data.items;
      meta = (result as any).data.meta || {};
    } else if (Array.isArray(result)) {
      items = result;
      meta = { page: 1, totalItems: result.length, totalPages: 1, itemCount: result.length };
    } else {

      items = [];
      meta = { page: 1, totalItems: 0, totalPages: 0, itemCount: 0 };
    }

    // Helper function to get local date string (YYYY-MM-DD) from a Date object
    const getLocalDateString = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Helper function to get local time string (HH:MM) from a Date object
    const getLocalTimeString = (date: Date): string => {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    // Validate and sanitize appointment data
    const sanitizedItems = items.map((appointment: any) => {
      // Convert startTime to local date/time if needed for fallback
      const startDate = appointment.startTime ? new Date(appointment.startTime) : null;

      return {
        ...appointment,
        // Ensure required fields have default values
        title: appointment.title || 'Untitled Appointment',
        // Legacy fields - populate from new fields if available, otherwise use defaults
        // Use local date/time to avoid timezone shifts
        date: appointment.date || (startDate ? getLocalDateString(startDate) : getLocalDateString(new Date())),
        time: appointment.time || (startDate ? getLocalTimeString(startDate) : '00:00'),
        notes: appointment.notes || appointment.description || '',
        // New fields - keep for backend processing
        description: appointment.description || appointment.notes || '',
        startTime: appointment.startTime || undefined,
        endTime: appointment.endTime || undefined,
        meetingLink: appointment.meetingLink || '',
        leadId: appointment.leadId || undefined,
        agencyId: appointment.agencyId || undefined,
        priorityStatus: appointment.priorityStatus || 'medium',
        associations: appointment.associations || [],
      };
    });

    return {
      statusCode: 200,
      message: 'Appointments fetched successfully',
      data: {
        meta: {
          page: meta.page || 1,
          totalItems: meta.totalItems || sanitizedItems.length,
          totalPages: meta.totalPages || 1,
          itemCount: meta.itemCount || sanitizedItems.length,
          nextPage: meta.nextPage || null,
          prevPage: meta.prevPage || null,
          offset: meta.offset || 0
        }
      },
      items: sanitizedItems,
      timestamp: new Date().toISOString(),
      timeTakenMs: 0,
      path: '/appointments'
    };
  } catch (error) {
    handleAppointmentError(error, 'fetch appointments', { toast: false });
    throw error; // Re-throw for caller handling
  }
};

// Get appointment by ID
export const getAppointmentById = async (appointmentId: string): Promise<GetAppointmentByIdResponse> => {
  try {
    // Validate appointment ID
    if (!appointmentId || appointmentId.trim() === '') {
      throw new Error('Appointment ID is required');
    }

    const result = await handleApiCall<GetAppointmentByIdResponse>(async () => {
      const response = await api.get<GetAppointmentByIdResponse>({
        url: getApiUrl(endpoint.appointments.getAppointmentById(appointmentId)),
        auth: true, // Add authentication
      });
      return response;
    });

    // Validate response structure
    if (!result.data) {
      throw new Error('Invalid response format from appointment API');
    }

    // Helper function to get local date string (YYYY-MM-DD) from a Date object
    const getLocalDateString = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Helper function to get local time string (HH:MM) from a Date object
    const getLocalTimeString = (date: Date): string => {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    // Ensure appointment data has required fields
    const appointmentData = (result as any).data?.data || (result as any).data || result;
    if (appointmentData) {
      // Convert startTime to local date/time if needed for fallback
      const startDate = appointmentData.startTime ? new Date(appointmentData.startTime) : null;

      const enhancedData = {
        ...appointmentData,
        title: appointmentData.title || 'Untitled Appointment',
        // Legacy fields - populate from new fields if available, otherwise use defaults
        // Use local date/time to avoid timezone shifts
        date: appointmentData.date || (startDate ? getLocalDateString(startDate) : getLocalDateString(new Date())),
        time: appointmentData.time || (startDate ? getLocalTimeString(startDate) : '00:00'),
        notes: appointmentData.notes || appointmentData.description || '',
        // New fields - keep for backend processing
        description: appointmentData.description || appointmentData.notes || '',
        startTime: appointmentData.startTime || undefined,
        endTime: appointmentData.endTime || undefined,
        meetingLink: appointmentData.meetingLink || '',
        leadId: appointmentData.leadId || undefined,
        agencyId: appointmentData.agencyId || undefined,
        priorityStatus: appointmentData.priorityStatus || 'medium',
        associations: appointmentData.associations || [],
      };

      // Update the result with enhanced data
      (result as any).data = enhancedData;
    }

    return (result as any).data;
  } catch (error) {
    handleAppointmentError(error, `fetch appointment ${appointmentId}`);
    throw error;
  }
};

// Create new appointment
export const createAppointment = async (
  appointmentData: CreateAppointmentParams
): Promise<GetAppointmentByIdResponse> => {
  try {
    // Validate appointment data
    validateAppointmentData(appointmentData, 'create');

    // Sanitize and prepare data for backend (ONLY send fields backend expects)
    // Use the date and time as provided without timezone conversion to avoid date shifts
    // Generate fallback date in local timezone to avoid UTC date shift
    const now = new Date();
    const localFallbackDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const apptDate = appointmentData.apptDate || localFallbackDate;
    const apptTime = appointmentData.time || '09:00';

    // Create startTime by parsing date and time components explicitly in local timezone
    // This avoids browser inconsistencies with ISO string parsing
    const [year, month, day] = apptDate.split('-').map(Number);
    const [hours, minutes] = apptTime.split(':').map(Number);
    const localDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);

    const startTimeDate = appointmentData.startTime
      ? new Date(appointmentData.startTime)
      : localDateTime;

    // Default appointment duration: 30 minutes (allows 15-min slot bookings)
    // If duration is provided, use it; otherwise default to 30 minutes
    const durationMs = appointmentData.duration
      ? appointmentData.duration * 60 * 1000  // duration in minutes -> ms
      : 30 * 60 * 1000; // 30 minutes default

    const endTimeDate = appointmentData.endTime
      ? new Date(appointmentData.endTime)
      : new Date(startTimeDate.getTime() + durationMs);

    const sanitizedData: any = {
      title: appointmentData.title.trim(),

      // REQUIRED LEGACY FIELDS (backend validator requires these)
      // Keep date and time in the format provided by the user (local timezone)
      time: apptTime, // HH:MM format - use the original time input
      apptDate: apptDate, // YYYY-MM-DD format - use the original date input

      // NEW FIELDS (backend also processes these)
      startTime: startTimeDate.toISOString(),
      endTime: endTimeDate.toISOString(),

      // Default status for new appointments
      status: appointmentData.status || 'scheduled',
    };

    // Optional fields (only include if provided)
    if (appointmentData.description || appointmentData.apptNotes) {
      sanitizedData.description = (appointmentData.description || appointmentData.apptNotes)?.trim();
    }
    if (appointmentData.apptNotes) {
      sanitizedData.apptNotes = appointmentData.apptNotes.trim();
    }
    if (appointmentData.meetingLink) {
      sanitizedData.meetingLink = appointmentData.meetingLink.trim();
    }
    if (appointmentData.location) {
      sanitizedData.location = appointmentData.location.trim();
    }
    if (appointmentData.leadId) {
      sanitizedData.leadId = appointmentData.leadId.trim();
    }
    if (appointmentData.agencyId) {
      sanitizedData.agencyId = appointmentData.agencyId.trim();
    }
    if (appointmentData.userId) {
      sanitizedData.userId = appointmentData.userId.trim();
    }
    if (appointmentData.priorityStatus) {
      sanitizedData.priorityStatus = appointmentData.priorityStatus;
    }
    if (appointmentData.type) {
      sanitizedData.type = appointmentData.type;
    }
    if (appointmentData.duration !== undefined) {
      sanitizedData.duration = appointmentData.duration;
    }

    // Email confirmation flag
    if (appointmentData.sendEmailConf !== undefined) {
      sanitizedData.sendEmailConf = appointmentData.sendEmailConf;
    }

    // Associations (properly formatted for backend - ONLY send fields backend expects)
    if (appointmentData.associations && appointmentData.associations.length > 0) {
      sanitizedData.associations = appointmentData.associations.map(assoc => ({
        entityType: assoc.entityType,
        entityId: assoc.entityId,
        // DO NOT send entityName - backend validator doesn't accept it
        ...(assoc.notes && { notes: assoc.notes.trim() }),
        ...(assoc.metadata && { metadata: assoc.metadata })
      }));

      // Extract leadId from associations for cache invalidation
      const leadAssociation = appointmentData.associations.find(
        assoc => assoc.entityType.toLowerCase() === 'lead'
      );
      if (leadAssociation && !sanitizedData.leadId) {
        sanitizedData.leadId = leadAssociation.entityId;
      }
    } else if (appointmentData.leadId) {
      // If no associations but leadId provided, create association automatically
      sanitizedData.associations = [{
        entityType: 'lead',
        entityId: appointmentData.leadId.trim(),
      }];
    }

    const result = await handleApiCall<GetAppointmentByIdResponse>(async () => {
      const response = await api.post<GetAppointmentByIdResponse>({
        url: getApiUrl(endpoint.appointments.getAppointments),
        body: sanitizedData,
        auth: true,
      });
      return response;
    });

    // handleApiCall already extracts and returns the data
    return result;
  } catch (error) {
    handleAppointmentError(error, 'create appointment');
    throw error;
  }
};

// Update appointment
export const updateAppointment = async (
  appointmentId: string,
  appointmentData: Partial<UpdateAppointment>
): Promise<GetAppointmentByIdResponse> => {
  try {
    // Validate appointment ID
    if (!appointmentId || appointmentId.trim() === '') {
      throw new Error('Appointment ID is required for update');
    }

    // Validate appointment data
    validateAppointmentData(appointmentData, 'update');

    // Sanitize update data
    const sanitizedData: Partial<UpdateAppointment> = {};

    if (appointmentData.title !== undefined) {
      sanitizedData.title = appointmentData.title.trim();
    }
    // Status field
    if (appointmentData.status !== undefined) {
      sanitizedData.status = appointmentData.status;
    }
    // Legacy fields
    if (appointmentData.apptNotes !== undefined) {
      sanitizedData.apptNotes = appointmentData.apptNotes?.trim() || '';
    }
    if (appointmentData.time !== undefined) {
      sanitizedData.time = appointmentData.time;
    }
    if (appointmentData.apptDate !== undefined) {
      sanitizedData.apptDate = appointmentData.apptDate;
    }
    // duration was absent from this allowlist, so an edited length never left
    // the browser — the request simply didn't carry the field.
    {
      const d = Number((appointmentData as Record<string, unknown>).duration);
      if (Number.isFinite(d) && d > 0) {
        (sanitizedData as unknown as Record<string, unknown>).duration = d;
      }
    }

    // Auto-compute startTime from legacy time + apptDate when they change
    // This keeps startTime in sync with the legacy fields
    {
      const maybe = appointmentData as Record<string, unknown>;
      const hasExplicitStartTime = maybe.startTime !== undefined;

      if (!hasExplicitStartTime && (appointmentData.time || appointmentData.apptDate)) {
        const apptTime = appointmentData.time;
        const apptDate = appointmentData.apptDate;
        if (apptTime && apptDate) {
          const [year, month, day] = apptDate.split('-').map(Number);
          const [hours, minutes] = apptTime.split(':').map(Number);
          const localDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
          if (!isNaN(localDateTime.getTime())) {
            (sanitizedData as unknown as Record<string, unknown>).startTime = localDateTime.toISOString();
            // Derive endTime from the ACTUAL duration. This was hard-coded to
            // 30 minutes, so every save stamped a 30-minute end time no matter
            // what length the user picked.
            if (maybe.endTime === undefined) {
              const mins = Number(maybe.duration) > 0 ? Number(maybe.duration) : 30;
              (sanitizedData as unknown as Record<string, unknown>).endTime =
                new Date(localDateTime.getTime() + mins * 60 * 1000).toISOString();
            }
          }
        }
      }
    }

    // New fields
    {
      const maybe = appointmentData as Record<string, unknown>;
      const desc = maybe.description;
      if (desc !== undefined) {
        (sanitizedData as unknown as Record<string, unknown>).description =
          typeof desc === 'string' ? desc.trim() : '';
      }

      const st = maybe.startTime;
      if (st !== undefined) {
        const iso = st
          ? new Date(typeof st === 'string' || st instanceof Date ? st : String(st)).toISOString()
          : undefined;
        (sanitizedData as unknown as Record<string, unknown>).startTime = iso;
      }

      const en = maybe.endTime;
      if (en !== undefined) {
        const iso = en
          ? new Date(typeof en === 'string' || en instanceof Date ? en : String(en)).toISOString()
          : undefined;
        (sanitizedData as unknown as Record<string, unknown>).endTime = iso;
      }

      const link = maybe.meetingLink;
      if (link !== undefined) {
        (sanitizedData as unknown as Record<string, unknown>).meetingLink =
          typeof link === 'string' ? link.trim() : '';
      }

      const leadId = maybe.leadId;
      if (leadId !== undefined) {
        (sanitizedData as unknown as Record<string, unknown>).leadId =
          typeof leadId === 'string' ? leadId.trim() : undefined;
      }

      const agencyId = maybe.agencyId;
      if (agencyId !== undefined) {
        (sanitizedData as unknown as Record<string, unknown>).agencyId =
          typeof agencyId === 'string' ? agencyId.trim() : undefined;
      }

      const prio = maybe.priorityStatus;
      if (prio !== undefined) {
        (sanitizedData as unknown as Record<string, unknown>).priorityStatus =
          typeof prio === 'string' ? prio : 'medium';
      }
    }
    if (appointmentData.associations !== undefined) {
      sanitizedData.associations = appointmentData.associations?.map(assoc => ({
        ...assoc,
        notes: assoc.notes?.trim() || '',
        metadata: assoc.metadata || {}
      })) || [];
    }

    const result = await handleApiCall<GetAppointmentByIdResponse>(async () => {
      const response = await api.patch({
        url: getApiUrl(endpoint.appointments.getAppointmentById(appointmentId)),
        body: sanitizedData,
        auth: true, // Add authentication
      });
      return response;
    });

    // Handle different response structures - backend may return data directly or wrapped
    if ((result as any)?.data) {
      return (result as any).data;
    } else if (result) {
      // If result is the appointment itself (not wrapped in data), return it
      return result;
    } else {
      throw new Error('Invalid response format from update appointment API');
    }
  } catch (error) {
    handleAppointmentError(error, `update appointment ${appointmentId}`);
    throw error;
  }
};

// Delete appointment
export const deleteAppointment = async (appointmentId: string): Promise<void> => {
  try {
    // Validate appointment ID
    if (!appointmentId || appointmentId.trim() === '') {
      throw new Error('Appointment ID is required for deletion');
    }

    await handleApiCall(async () => {
      const response = await api.delete({
        url: getApiUrl(endpoint.appointments.getAppointmentById(appointmentId)),
        auth: true, // Add authentication
      });
      return response;
    });

    // Success - no need to return anything for delete operations
  } catch (error) {
    handleAppointmentError(error, `delete appointment ${appointmentId}`);
    throw error;
  }
};

// Get appointment statistics (enhanced version)
export const getAppointmentStats = async (): Promise<{
  total: number;
  pending: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  noShow: number;
  scheduled: number;
  thisWeek: number;
  thisMonth: number;
}> => {
  try {
    // Try to get stats from dedicated endpoint first
    try {
      const result = await handleApiCall<{
        total: number;
        pending: number;
        confirmed: number;
        completed: number;
        cancelled: number;
        noShow: number;
        scheduled: number;
        thisWeek: number;
        thisMonth: number;
      }>(async () => {
        const response = await api.get<{
          total: number;
          pending: number;
          confirmed: number;
          completed: number;
          cancelled: number;
          noShow: number;
          scheduled: number;
          thisWeek: number;
          thisMonth: number;
        }>({
          url: getApiUrl(endpoint.appointments.getAppointmentsStats),
          auth: true, // Add authentication
        });
        return response;
      });

      if ((result as any).data) {
        return (result as any).data;
      }
    } catch (statsError) {

    }

    // Fallback: calculate stats from appointments list
    const response = await getAppointments({ page: 1, limit: 1000 });
    const appointments = response.items;

    // Helper to parse appointment date in local timezone
    const parseAppointmentDate = (a: any): Date | null => {
      if (a.startTime) {
        return new Date(a.startTime);
      }
      if (a.date) {
        // Parse YYYY-MM-DD as local date to avoid UTC shift
        const dateStr = typeof a.date === 'string' ? a.date : String(a.date);
        if (dateStr.includes('T')) {
          return new Date(dateStr);
        }
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day, 12, 0, 0, 0); // Noon to avoid DST issues
      }
      return null;
    };

    const now = new Date();
    // Create fresh date objects to avoid mutation issues
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      total: appointments.length,
      pending: appointments.filter(a => a.status === 'pending').length,
      confirmed: appointments.filter(a => a.status === 'confirmed').length,
      completed: appointments.filter(a => a.status === 'completed').length,
      cancelled: appointments.filter(a => a.status === 'cancelled').length,
      // 'no_show' (underscore) is the real stored value — appointmentStatusOptions
      // and the DB both use it. Comparing to 'no-show' made this stat always 0.
      noShow: appointments.filter(a => a.status === 'no_show').length,
      scheduled: appointments.filter(a => a.status === 'scheduled').length,
      thisWeek: appointments.filter(a => {
        const appointmentDate = parseAppointmentDate(a);
        return appointmentDate && appointmentDate >= startOfWeek;
      }).length,
      thisMonth: appointments.filter(a => {
        const appointmentDate = parseAppointmentDate(a);
        return appointmentDate && appointmentDate >= startOfMonth;
      }).length,
    };
  } catch (error) {
    handleAppointmentError(error, 'fetch appointment statistics', { toast: false });
    throw error;
  }
};

// Bulk operations for appointments
export const bulkUpdateAppointments = async (
  appointmentIds: string[],
  updateData: Partial<UpdateAppointment>
): Promise<void> => {
  try {
    if (!appointmentIds.length) {
      throw new Error('No appointment IDs provided for bulk update');
    }

    validateAppointmentData(updateData, 'update');

    // Process in batches to avoid overwhelming the server
    const batchSize = 10;
    const batches = [];
    
    for (let i = 0; i < appointmentIds.length; i += batchSize) {
      batches.push(appointmentIds.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      await Promise.all(
        batch.map(id => updateAppointment(id, updateData))
      );
    }
  } catch (error) {
    handleAppointmentError(error, 'bulk update appointments');
    throw error;
  }
};

export const bulkDeleteAppointments = async (appointmentIds: string[]): Promise<void> => {
  try {
    if (!appointmentIds.length) {
      throw new Error('No appointment IDs provided for bulk delete');
    }

    // Process in batches
    const batchSize = 10;
    const batches = [];
    
    for (let i = 0; i < appointmentIds.length; i += batchSize) {
      batches.push(appointmentIds.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      await Promise.all(
        batch.map(id => deleteAppointment(id))
      );
    }
  } catch (error) {
    handleAppointmentError(error, 'bulk delete appointments');
    throw error;
  }
};

// ── Agency calendar (owner/admin) ────────────────────────────────────────────

export interface AgencyCalendarItem {
  id: string;
  title: string;
  status?: string;
  type?: string;
  startTime: string;
  endTime?: string;
  date?: string;
  time?: string;
  duration?: number;
  location?: string;
  notes?: string;
  agencyId?: string;
  agencyName?: string;
  leadId?: string;
  leadName?: string | null;
  userId: string;
  bookedByName?: string;
  /** True when the Appointment API (Live Transfer AI) created it. Then
   *  `bookedByName` is the triage OWNER it was parked on, not who booked it. */
  bookedViaApi?: boolean;
  bookedByRole?: string;
  assignedToUserId?: string | null;
  assignedToName?: string | null;
  assignedAt?: string | null;
  /** Computed server-side from agency roles — drives the calendar colours. */
  ownerKind: "agent" | "unassigned" | "owner";
}

export interface AgencyCalendarResponse {
  items: AgencyCalendarItem[];
  isPrivileged: boolean;
  agencyIds: string[];
  range: { start: string; end: string };
}

export const getAgencyCalendar = async (params: {
  start?: string;
  end?: string;
  includeSubAgencies?: boolean;
}): Promise<AgencyCalendarResponse> => {
  const q = new URLSearchParams();
  if (params.start) q.set("start", params.start);
  if (params.end) q.set("end", params.end);
  if (params.includeSubAgencies) q.set("includeSubAgencies", "true");
  const res = await api.get<{ data?: AgencyCalendarResponse } | AgencyCalendarResponse>({
    url: `${getApiUrl(endpoint.appointments.getAgencyCalendar)}?${q.toString()}`,
    auth: true,
  });
  const env = (res as { data?: unknown })?.data ?? res;
  const e = env as { data?: AgencyCalendarResponse } & AgencyCalendarResponse;
  const payload = (e?.data ?? e) as AgencyCalendarResponse;
  return {
    items: payload?.items ?? [],
    isPrivileged: !!payload?.isPrivileged,
    agencyIds: payload?.agencyIds ?? [],
    range: payload?.range ?? { start: "", end: "" },
  };
};

export const assignAppointment = async (
  appointmentId: string,
  assignedToUserId: string | null,
): Promise<unknown> => {
  const res = await api.patch<unknown>({
    url: getApiUrl(endpoint.appointments.assignAppointment(appointmentId)),
    body: { assignedToUserId },
    auth: true,
  });
  return (res as { data?: unknown })?.data ?? res;
};
